/**
 * Integration Marketplace Demo — initial fixture snapshot.
 *
 * THROWAWAY DEMO CODE (see ./README.md). Seeds the store with one already-
 * connected source (IBM i / AS/400, matching this org's real backbone) whose
 * Products link is already ingested — so the Data Sources page and the item
 * catalog have content on first load. The live demo then connects a NEW source
 * (e.g. SAP S/4HANA) on stage for the "wow" moment.
 */

import { getIntegration } from './integration-catalog';
import { buildBaselineCatalog, toConnectedLink } from './ingest';
import { minutesAgoIso } from './time';
import type { ConnectedLink, ConnectedSource, ImDemoState } from './types';

export function buildImDemoSeed(): ImDemoState {
  const as400 = getIntegration('as400');
  if (!as400) {
    return { sources: [], catalog: [], workflows: [] };
  }

  const sourceId = 'src_as400_seed';
  const productsWorkflowId = 'wf_as400_products';
  // The established AS/400 source last synced about an hour ago.
  const seedSyncedAt = minutesAgoIso(64);

  const links: ConnectedLink[] = as400.detectedLinks.map((link) => {
    if (link.entity === 'products') {
      return {
        ...toConnectedLink(link, 'ingested', seedSyncedAt),
        ingestionWorkflowId: productsWorkflowId,
      };
    }
    // Secondary links are synced but not yet ingested into Gently's schema.
    return toConnectedLink(link, 'synced', seedSyncedAt);
  });

  const source: ConnectedSource = {
    id: sourceId,
    integrationId: as400.id,
    name: 'IBM i (AS/400) — Production',
    environment: 'Production',
    connectedAt: '2026-05-02T09:12:00Z',
    status: 'connected',
    links,
  };

  // The baseline item master = what AS/400 has already loaded into Gently.
  const productsLink = links.find((l) => l.entity === 'products');
  const catalog = productsLink
    ? buildBaselineCatalog(source, productsLink)
    : [];

  return {
    sources: [source],
    catalog,
    workflows: productsLink
      ? [
          {
            id: productsWorkflowId,
            sourceId,
            sourceLinkId: productsLink.id,
            title: 'Ingest IBM i (AS/400) → Products',
            targetEntity: 'products',
            createdAt: '2026-05-02T09:12:00Z',
            lastSyncedAt: seedSyncedAt,
            recordsIngested: productsLink.rowCount,
            matched: 0,
            inserted: catalog.length,
          },
        ]
      : [],
  };
}
